"use client";

import React, { FC, useEffect, useState } from "react";
import dynamic from "next/dynamic";
import axios from "axios";
import SocialsList from "@/shared/SocialsList";

// "CARNET DE VOYAGE" newsletter — same component the account pages use.
// Lazy + SSR-off because it sits below the fold.
const SectionNewsletter = dynamic(
  () => import("../(home)/desktop/SectionNewsletter"),
  {
    ssr: false,
    loading: () => <div style={{ minHeight: 220 }} aria-hidden="true" />,
  }
);
import { useAppSelector, useAppDispatch } from '@/app/lib/hooks';
import { useTranslation } from "react-i18next";
import { sendPublicSiteRequest, reasonKeyFor } from '@/app/lib/siteRequests';
import { setNotification } from '@/app/lib/reducers/notificationsReducer';

const API_URL = process.env.NEXT_PUBLIC_SOCKET ? `${process.env.NEXT_PUBLIC_SOCKET}/api` : 'http://localhost:3001/api';

// ----- inline line-icons (match the account section's icon language) ----
const ContactIcon: FC<{ name: string; size?: number; color?: string }> = ({
  name,
  size = 18,
  color = "currentColor",
}) => {
  const c = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none" as const,
    stroke: color,
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": "true" as const,
  };
  switch (name) {
    case "map-pin":
      return (
        <svg {...c}>
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
      );
    case "mail":
      return (
        <svg {...c}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="m3 7 9 6 9-6" />
        </svg>
      );
    case "whatsapp":
      return (
        <svg {...c}>
          <path d="M21 11.5a8.5 8.5 0 0 1-12.5 7.5L3 20l1.1-5.4A8.5 8.5 0 1 1 21 11.5z" />
          <path d="M8.5 9c0 4 2.5 6.5 6.5 6.5" />
        </svg>
      );
    case "globe":
      return (
        <svg {...c}>
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
        </svg>
      );
    default:
      return null;
  }
};

const formatWhatsappNumber = (raw?: string) => {
  if (!raw) return '';
  const match = raw.match(/^\+(\d{1,3})(\d{3})(\d{3})(\d{4})$/);
  if (match) return `+${match[1]} ${match[2]} ${match[3]} ${match[4]}`;
  return raw;
};

const buildWhatsappLink = (raw?: string) => {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  return digits ? `https://wa.me/${digits}` : '';
};

export interface PageContactProps {
  /**
   * Monte le contact DANS une autre page — aujourd'hui l'ecran « Messages » du
   * compte, qui garde son menu a gauche. L'entete et la bande newsletter sont
   * alors masques : la coquille du compte affiche deja fil d'Ariane, titre et
   * sous-titre, et la newsletter appartient a la page publique.
   *
   * Le formulaire, lui, est le meme — c'est tout l'interet : une seule
   * implementation, donc pas de derive entre les deux acces.
   */
  embedded?: boolean;
}

const PageContact: FC<PageContactProps> = ({ embedded = false }) => {
  const [contactType, setContactType] = useState("");
  const connectedUser = useAppSelector(state => state.auth.user);
  const selectedLanguage = useAppSelector(state => state.language.selectedLanguage);
  const { t } = useTranslation("guydeez");
  const dispatch = useAppDispatch();

  // States for form fields, initialized from connectedUser if present
  const [fullName, setFullName] = useState(
    connectedUser?.firstName && connectedUser?.lastName
      ? `${connectedUser.firstName} ${connectedUser.lastName}`
      : ""
  );
  const [email, setEmail] = useState(connectedUser?.email || "");
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [whatsappNumbers, setWhatsappNumbers] = useState<{ GUIDES?: string; CUSTOMER?: string }>({});

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_URL}/whatsapp/public-numbers`)
      .then((response) => {
        if (cancelled) return;
        if (response.data?.success) {
          setWhatsappNumbers(response.data.numbers || {});
        }
      })
      .catch(() => {
        // Silent failure: contact page still renders without numbers
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Connecte : le type vient du compte (users.role), plus de liste « Je suis
  // un(e) » a remplir — on sait deja qui ecrit. role 3 = guide, tout autre role
  // ecrit comme client. Non connecte : la liste reste, seul indice disponible.
  const connectedRole = connectedUser ? (connectedUser.role ?? connectedUser.roleDetails?.id) : undefined;
  const lockedContactType = connectedUser ? (connectedRole === 3 ? "guide" : "customer") : null;
  const effectiveContactType = lockedContactType ?? contactType;
  // Compte pro : exactement le formulaire de /contact-pro (motifs client, envoi
  // `contact-pro`, sans cle de motif — l'equipe info@ le recoit comme depuis
  // cette page).
  const isProContact = Boolean(connectedUser?.isPro) && connectedRole !== 3;

  const handleContactTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setContactType(e.target.value);
    setReason(""); // Reset reason on contact type change
  };

  const info = [
    {
      icon: "map-pin",
      title: t('contact_address'),
      desc: "IFZA Business Park, DDP, PO Box 342001, Dubai, United Arab Emirates",
    },
    {
      icon: "mail",
      title: t('contact_email'),
      desc: "info@guydeez.com",
      href: "mailto:info@guydeez.com",
    },
    {
      icon: "whatsapp",
      title: t('contact_whatsapp_guides'),
      desc: formatWhatsappNumber(whatsappNumbers.GUIDES),
      href: buildWhatsappLink(whatsappNumbers.GUIDES),
    },
    {
      icon: "whatsapp",
      title: t('contact_whatsapp_customer'),
      desc: formatWhatsappNumber(whatsappNumbers.CUSTOMER),
      href: buildWhatsappLink(whatsappNumbers.CUSTOMER),
    },
  ].filter((item) => item.desc); // drop WhatsApp cards while numbers load

  // Submit logic
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const missingFields: string[] = [];
    if (!fullName.trim()) missingFields.push(t("contact_full_name"));
    if (!email.trim()) missingFields.push(t("contact_email_address"));
    if (!reason.trim()) missingFields.push(t("contact_reasons"));
    if (!message.trim()) missingFields.push(t("contact_message"));

    if (missingFields.length > 0) {
      const notificationMessage = t(
        "Please fill in the following fields before submitting: {{fields}}",
        { fields: missingFields.join(", ") }
      );
      dispatch(setNotification({ type: "error", message: notificationMessage }));
      return;
    }

    setLoading(true);
    try {
      // Mail a l'equipe (info@ ou locals@ selon le motif, choisi par le backend)
      // et accuse de reception, depuis les modeles CMS.
      await sendPublicSiteRequest(isProContact
        ? {
            kind: 'contact',
            source: 'contact-pro',
            fullName: fullName.trim(),
            email: email.trim(),
            reason,
            message,
          }
        : {
            kind: 'contact',
            source: 'contact',
            fullName: fullName.trim(),
            email: email.trim(),
            reason,
            reasonKey: reasonKeyFor(reason, t),
            message,
          }, selectedLanguage);

      dispatch(setNotification({ type: "success", message: t("contact_email_sent_successfully") }));
      setFullName("");
      setEmail("");
      setReason("");
      setMessage("");
    } catch (err) {
      dispatch(setNotification({ type: "error", message: t("contact_email_sent_failed") }));
    } finally {
      setLoading(false);
    }
  };

  const cardStyle: React.CSSProperties = {
    padding: 24,
    background: "white",
    borderColor: "transparent",
    boxShadow: "var(--g-shadow-sm, 0 2px 6px rgba(0,0,0,.04))",
  };

  return (
    <div
      className="nc-PageContact"
      style={embedded ? undefined : { background: "var(--g-cream)" }}
    >
      <div
        className={embedded ? "" : "max-w-[1180px] mx-auto"}
        style={embedded ? undefined : { padding: "32px 20px 56px" }}
      >
        {/* Header */}
        {!embedded && (
        <div style={{ marginBottom: 28 }}>
          <div
            style={{
              fontSize: 12,
              color: "var(--g-coral, #C13B5C)",
              fontWeight: 700,
              letterSpacing: ".1em",
              marginBottom: 6,
            }}
          >
            {t("contact_eyebrow", { defaultValue: "CONTACT" })}
          </div>
          <h1
            style={{
              fontSize: "clamp(28px, 6vw, 40px)",
              color: "var(--g-burgundy)",
              fontWeight: 800,
              lineHeight: 1.1,
            }}
          >
            {t("contact_title")}
          </h1>
          <p
            style={{
              fontSize: 14,
              color: "var(--g-ink-500)",
              marginTop: 6,
              maxWidth: 520,
            }}
          >
            {t("contact_subtitle", {
              defaultValue:
                "Une question sur une visite, une réservation ou pour devenir guide ? Écrivez-nous, nous répondons en moins de 30 min, 7j/7.",
            })}
          </p>
        </div>
        )}

        <div
          className="grid grid-cols-1 lg:grid-cols-[340px_1fr]"
          style={{ gap: 24, alignItems: "start" }}
        >
          {/* Contact info — quick tappable cards (WhatsApp/email first for
              mobile so users can reach us in one tap). */}
          <aside className="flex flex-col" style={{ gap: 12 }}>
            {info.map((item, index) => {
              const inner = (
                <>
                  <span
                    className="grid place-items-center"
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: 12,
                      background: "var(--g-cream-deep, #F6E8D2)",
                      color: "var(--g-burgundy)",
                      flexShrink: 0,
                    }}
                  >
                    <ContactIcon name={item.icon} size={18} />
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <span
                      style={{
                        display: "block",
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: ".05em",
                        textTransform: "uppercase",
                        color: "var(--g-ink-500)",
                        marginBottom: 2,
                      }}
                    >
                      {item.title}
                    </span>
                    <span
                      style={{
                        display: "block",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--g-ink-900, var(--g-burgundy))",
                        wordBreak: "break-word",
                      }}
                    >
                      {item.desc}
                    </span>
                  </span>
                </>
              );
              const rowStyle: React.CSSProperties = {
                display: "flex",
                alignItems: "center",
                gap: 14,
                padding: 14,
                textDecoration: "none",
              };
              return item.href ? (
                <a
                  key={index}
                  href={item.href}
                  target={item.href.startsWith("http") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  className="gdx-card"
                  style={{ ...cardStyle, ...rowStyle }}
                >
                  {inner}
                </a>
              ) : (
                <div
                  key={index}
                  className="gdx-card"
                  style={{ ...cardStyle, ...rowStyle }}
                >
                  {inner}
                </div>
              );
            })}

            {/* Socials */}
            <div className="gdx-card" style={{ ...cardStyle, padding: 18 }}>
              <div
                className="flex items-center"
                style={{ gap: 10, marginBottom: 12 }}
              >
                <span
                  className="grid place-items-center"
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 10,
                    background: "var(--g-cream-deep, #F6E8D2)",
                    color: "var(--g-burgundy)",
                  }}
                >
                  <ContactIcon name="globe" size={16} />
                </span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: ".05em",
                    textTransform: "uppercase",
                    color: "var(--g-ink-500)",
                  }}
                >
                  {t("contact_socials")}
                </span>
              </div>
              <SocialsList />
            </div>
          </aside>

          {/* Form */}
          <div className="gdx-card" style={{ ...cardStyle, padding: 28 }}>
            <form
              onSubmit={handleSubmit}
              style={{ display: "flex", flexDirection: "column", gap: 18 }}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: 16 }}>
                <div>
                  <label className="gdx-label" htmlFor="contact-name">
                    {t("contact_full_name")}
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    className="gdx-input"
                    placeholder={t("contact_type_your_name") as string}
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    autoComplete="name"
                  />
                </div>
                <div>
                  <label className="gdx-label" htmlFor="contact-email">
                    {t("contact_email_address")}
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    className="gdx-input"
                    placeholder="example@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                  />
                </div>
              </div>

              {!lockedContactType && (
              <div>
                <label className="gdx-label" htmlFor="contact-type">
                  {t("contact_i_am_a")}
                </label>
                <select
                  id="contact-type"
                  className="gdx-select"
                  value={contactType}
                  onChange={handleContactTypeChange}
                >
                  <option value="" disabled>
                    {t("contact_select_an_option")}
                  </option>
                  <option value="customer">
                    {t("contact_for_customers_option")}
                  </option>
                  <option value="guide">{t("contact_for_guides_option")}</option>
                </select>
              </div>
              )}

              {effectiveContactType === "customer" && (
                <div>
                  <label className="gdx-label" htmlFor="contact-reason">
                    {t("contact_reasons")}
                  </label>
                  <select
                    id="contact-reason"
                    className="gdx-select"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  >
                    <option value="">{t("contact_select_an_option")}</option>
                    <option value={t("contact_reasons_request_quote")}>{t("contact_reasons_request_quote")}</option>
                    <option value={t("contact_reasons_question_booking")}>{t("contact_reasons_question_booking")}</option>
                    <option value={t("contact_reasons_edit_cancel_booking")}>{t("contact_reasons_edit_cancel_booking")}</option>
                    <option value={t("contact_reasons_about_account")}>{t("contact_reasons_about_account")}</option>
                    <option value={t("contact_reasons_other_questions")}>{t("contact_reasons_other_questions")}</option>
                  </select>
                </div>
              )}
              {effectiveContactType === "guide" && (
                <div>
                  <label className="gdx-label" htmlFor="contact-reason">
                    {t("contact_reasons")}
                  </label>
                  <select
                    id="contact-reason"
                    className="gdx-select"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  >
                    <option value="">{t("contact_select_an_option")}</option>
                    <option value={t("contact_reasons_guide_about_mission")}>{t("contact_reasons_about_mission")}</option>
                    <option value={t("contact_reasons_guide_about_account")}>{t("contact_reasons_about_account")}</option>
                    <option value={t("contact_reasons_guide_general_asking")}>{t("contact_reasons_general_asking")}</option>
                  </select>
                </div>
              )}

              <div>
                <label className="gdx-label" htmlFor="contact-message">
                  {t("contact_message")}
                </label>
                <textarea
                  id="contact-message"
                  className="gdx-input"
                  rows={6}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  style={{ height: "auto", minHeight: 140, padding: "12px 14px", lineHeight: 1.5 }}
                />
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    height: 52,
                    width: "100%",
                    maxWidth: 280,
                    padding: "0 28px",
                    borderRadius: 999,
                    background: loading
                      ? "var(--g-ink-300, #cdb6a3)"
                      : "var(--g-green-ada, #1F7A45)",
                    color: "white",
                    border: 0,
                    fontWeight: 700,
                    fontSize: 15,
                    cursor: loading ? "wait" : "pointer",
                    boxShadow: loading
                      ? "none"
                      : "0 12px 26px rgba(31,122,69,.35)",
                    transition: "background .15s ease",
                  }}
                >
                  {loading
                    ? `${t("contact_send_message")}…`
                    : t("contact_send_message")}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* "CARNET DE VOYAGE" newsletter — page publique uniquement. */}
      {!embedded && <SectionNewsletter />}
    </div>
  );
};

export default PageContact;
